import {
  CheckboxLineField,
  CollapsibleSection,
  Flex,
  Text
} from '@sequencehq/core-components'
import { withoutPurpose } from 'CustomerContacts/utils/contactPurposes'
import { CustomerAddressFields } from '../components/CustomerAddressFields'
import { CustomerShippingContactField } from '../components/CustomerShippingContactField'
import { CustomerFormFieldControls } from '../types'
import { useSectionExpansion } from '../useSectionExpansion'
import { fieldStyles } from './styles'

export type ShippingInformationSectionProps = {
  /** The shipping address field controls — pass the hook's `fields` directly. */
  shippingAddressFields: Pick<
    CustomerFormFieldControls,
    | 'shippingAddress.country'
    | 'shippingAddress.line1'
    | 'shippingAddress.line2'
    | 'shippingAddress.town'
    | 'shippingAddress.state'
    | 'shippingAddress.postcode'
  >
  isShippingSameAsBillingField: CustomerFormFieldControls['isShippingSameAsBilling']
  /**
   * Renders the shipping contact select when passed. "Same as billing" then
   * covers the contact too, so the primary billing contact should be sent as
   * the shipping contact when it's ticked.
   */
  contactsField?: CustomerFormFieldControls['contacts']
  'data-testid'?: string
}

export const ShippingInformationSection = ({
  shippingAddressFields,
  isShippingSameAsBillingField,
  contactsField,
  'data-testid': testId
}: ShippingInformationSectionProps) => {
  const { isOpen, onToggle } = useSectionExpansion(
    [
      ...Object.values(shippingAddressFields),
      isShippingSameAsBillingField
    ].some(field => field.validationErrors.length > 0)
  )

  return (
    <CollapsibleSection
      title="Shipping information"
      isOpen={isOpen}
      onToggle={onToggle}
      data-testid={testId}
    >
      <Flex direction="column" gap="12px">
        <CheckboxLineField
          data-testid={testId ? `${testId}.sameAsBilling` : undefined}
          text={contactsField ? 'Same as billing' : 'Same as billing address'}
          variant="v2"
          value={isShippingSameAsBillingField.value}
          onChange={isSameAsBilling => {
            isShippingSameAsBillingField.onChange(isSameAsBilling)
            // Ticking the box drops any earlier pick, so unticking it again
            // starts from an empty select.
            if (isSameAsBilling && contactsField) {
              contactsField.onChange(
                contactsField.value.map(contact =>
                  withoutPurpose(contact, 'SHIPPING')
                )
              )
            }
          }}
          validationErrors={isShippingSameAsBillingField.validationErrors}
        />
        {!isShippingSameAsBillingField.value && (
          <>
            {contactsField && (
              <CustomerShippingContactField
                data-testid={testId ? `${testId}.shippingContact` : undefined}
                contactsField={contactsField}
              />
            )}
            <Flex direction="column" gap="8px">
              <Text {...fieldStyles.label}>Shipping details</Text>
              <CustomerAddressFields
                data-testid={testId ? `${testId}.shippingAddress` : undefined}
                fields={{
                  country: shippingAddressFields['shippingAddress.country'],
                  line1: shippingAddressFields['shippingAddress.line1'],
                  line2: shippingAddressFields['shippingAddress.line2'],
                  town: shippingAddressFields['shippingAddress.town'],
                  state: shippingAddressFields['shippingAddress.state'],
                  postcode: shippingAddressFields['shippingAddress.postcode']
                }}
                isCountryClearable
              />
            </Flex>
          </>
        )}
      </Flex>
    </CollapsibleSection>
  )
}
